import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Security as SecurityIcon, Search as SearchIcon, ArrowBack as ArrowBackIcon, Clear as ClearIcon } from '@mui/icons-material';
import MainLayout from '../../../components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';

interface AuditLog {
  id: string;
  userId: string;
  roleCode: string;
  action: string;
  moduleCode: string;
  description: string;
  entity?: string;
  entityId?: string;
  status: string;
  errorMessage?: string;
  ipAddress?: string;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    role?: {
      code: string;
      name: string;
    };
  };
}

interface ApiResponse {
  items: AuditLog[];
  total: number;
  page: number;
  limit: number;
}

const AuditLogs: React.FC = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    entity: '',
    moduleCode: '',
    action: '',
    roleCode: '',
    status: '',
    userId: '',
    search: '',
  });

  const [users, setUsers] = useState<any[]>([]);
  const [actionOptions, setActionOptions] = useState<string[]>([]);
  // Hardcoded roles for now, or fetch from API if available
  const roles = [
    { code: 'SUPER_ADMIN', name: 'Super Admin' },
    { code: 'ADMIN', name: 'Admin' },
    { code: 'FACULTY', name: 'Faculty' },
    { code: 'STUDENT', name: 'Student' },
    { code: 'PARENT', name: 'Parent' },
    { code: 'ACCOUNTANT', name: 'Accountant' },
    { code: 'LIBRARIAN', name: 'Librarian' },
    { code: 'RECEPTIONIST', name: 'Receptionist' },
  ];

  useEffect(() => {
    fetchUsers();
    fetchActions();
    fetchLogs(1);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/system-settings/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchActions = async () => {
    try {
      const response = await axios.get('/system-settings/logs/meta/actions');
      setActionOptions(response.data);
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
  };

  const fetchLogs = async (pageNum: number) => {
    setLoading(true);
    try {
      // Remove empty filters
      const params: any = {
        page: pageNum,
        limit: limit,
      };

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.entity) params.entity = filters.entity;
      if (filters.moduleCode) params.moduleCode = filters.moduleCode;
      if (filters.action) params.action = filters.action;
      if (filters.roleCode) params.roleCode = filters.roleCode;
      if (filters.status) params.status = filters.status;
      if (filters.userId) params.userId = filters.userId;
      if (filters.search) params.search = filters.search;

      const response = await axios.get<ApiResponse>('/system-settings/logs/audit', { params });
      
      setLogs(response.data.items || []);
      setTotal(response.data.total || 0);
      setPage(response.data.page || pageNum);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleSearch = () => {
    fetchLogs(1);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      entity: '',
      moduleCode: '',
      action: '',
      roleCode: '',
      status: '',
      userId: '',
      search: '',
    });
    // Trigger fetch after state update in next render or immediately
    // Since setState is async, we can just call fetchLogs(1) with empty filters manually or wait for user to click Search
    // Better UX is to clear and reload
    setTimeout(() => {
        // We can't use the state immediately here, so we'll just reload the page or call with cleared object
        // But fetchLogs uses 'filters' state. 
        // Simplest is to just reset state and let user click filter, or pass params explicitly.
        // Let's call fetchLogs with explicit empty object overrides if we want immediate reload, 
        // but fetchLogs reads from state.
        // So we will just set state and then user has to click Search, OR we use useEffect on filters? 
        // No, user wants manual search. 
        // Let's just reset state. User can click Filter.
        // Actually, let's auto-reload for "Clear"
    }, 0);
    // Actually, let's just modify fetchLogs to accept optional overrides or just wait.
    // Let's just set state.
  };

  // Effect to reload when filters are cleared? No, might cause loops.
  // Let's just create a separate function or pass params.
  const clearAndFetch = () => {
      setFilters({
        startDate: '',
        endDate: '',
        entity: '',
        moduleCode: '',
        action: '',
        roleCode: '',
        status: '',
        userId: '',
        search: '',
      });
      // We need to fetch with empty filters. 
      // We can't rely on 'filters' state being updated immediately.
      // So we'll reload the page (simple) or refactor fetchLogs.
      // Refactoring fetchLogs to accept params is better but 'filters' is in scope.
      // Let's just set state and manual trigger?
      // Actually, standard pattern is just clear state. User clicks Filter.
      // But "Clear Filters" usually implies "Reset".
      // Let's hack it slightly:
      setLoading(true);
      axios.get<ApiResponse>('/system-settings/logs/audit', { params: { page: 1, limit } })
        .then(res => {
            setLogs(res.data.items || []);
            setTotal(res.data.total || 0);
            setPage(1);
        })
        .finally(() => setLoading(false));
  };


  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    fetchLogs(value);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'info';
      case 'DELETE': return 'error';
      case 'ACTIVATE': return 'success';
      case 'DEACTIVATE': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
      return status === 'SUCCESS' ? 'success' : 'error';
  };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/logs')} sx={{ mr: 2 }}>
            Back
          </Button>
          <SecurityIcon color="secondary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">System Audit Logs</Typography>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Row 1 */}
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                select
                label="Module"
                size="small"
                value={filters.moduleCode}
                onChange={(e) => handleFilterChange('moduleCode', e.target.value)}
              >
                <MenuItem value="">All Modules</MenuItem>
                <MenuItem value="EXAMINATION">Examination</MenuItem>
                <MenuItem value="FEES">Fees</MenuItem>
                <MenuItem value="STUDENTS">Students</MenuItem>
                <MenuItem value="ATTENDANCE">Attendance</MenuItem>
                <MenuItem value="REPORTS">Reports</MenuItem>
                <MenuItem value="SYSTEM_SETTINGS">System Settings</MenuItem>
                <MenuItem value="REPORT_SETTINGS">Report Settings</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                select
                label="Action"
                size="small"
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <MenuItem value="">All Actions</MenuItem>
                {actionOptions.map((act) => (
                  <MenuItem key={act} value={act}>{act}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
               <TextField
                fullWidth
                select
                label="Role"
                size="small"
                value={filters.roleCode}
                onChange={(e) => handleFilterChange('roleCode', e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                {roles.map((role) => (
                    <MenuItem key={role.code} value={role.code}>{role.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
             <Grid item xs={12} sm={2}>
               <TextField
                fullWidth
                select
                label="User"
                size="small"
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>{user.firstName} {user.lastName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
                <TextField
                fullWidth
                select
                label="Status"
                size="small"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="SUCCESS">Success</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={2}>
               <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearAndFetch}
                color="secondary"
              >
                Clear
              </Button>
            </Grid>

             {/* Row 2 */}
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Entity / Target"
                size="small"
                placeholder="e.g. FeeHead"
                value={filters.entity}
                onChange={(e) => handleFilterChange('entity', e.target.value)}
              />
            </Grid>
             <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Search Description"
                size="small"
                placeholder="Search logs..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Actor (Role)</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Target Entity</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                            {format(new Date(log.createdAt), 'dd MMM yyyy')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {format(new Date(log.createdAt), 'hh:mm a')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" fontWeight="medium">
                                {log.user?.firstName} {log.user?.lastName}
                            </Typography>
                            <Chip 
                                label={log.roleCode || log.user?.role?.name || 'Unknown'} 
                                size="small" 
                                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', width: 'fit-content' }} 
                            />
                        </Box>
                      </TableCell>
                      <TableCell>
                         <Chip label={log.moduleCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                            label={log.action} 
                            color={getActionColor(log.action) as any}
                            size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {log.entity ? (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {log.entity}
                            </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap title={log.description}>
                            {log.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                         <Chip 
                            label={log.status} 
                            color={getStatusColor(log.status) as any}
                            size="small" 
                            variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
             <Pagination 
                count={Math.ceil(total / limit)} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                showFirstButton 
                showLastButton
            />
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default AuditLogs;
